import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const PROJECT_TYPES = [
  'Site vitrine',
  'Application web',
  'Automatisation / IA',
  'Autre',
];

const BUDGETS = [
  '< 1 000€',
  '1 000€ – 5 000€',
  '5 000€ – 10 000€',
  '10 000€+',
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: false, margin: '-10%' });
  const location = useLocation();

  const [form, setForm] = useState({
    name: '',
    email: '',
    project_type: '',
    budget: '',
    message: '',
    honeypot: '',
  });

  // Pre-fill from estimator navigation state
  useEffect(() => {
    const prefill = location.state?.prefill;
    if (prefill) {
      setForm((prev) => ({
        ...prev,
        project_type: PROJECT_TYPES.includes(prefill.project_type) ? prefill.project_type : 'Autre',
        message: prefill.message || prev.message,
      }));
    }
  }, [location.state]);
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');

    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      if (res.status === 429) {
        setStatus('error');
        setErrorMsg('Trop de messages envoyés. Réessayez dans quelques minutes.');
        return;
      }

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || 'Erreur serveur');
      }

      setStatus('success');
      setForm({ name: '', email: '', project_type: '', budget: '', message: '', honeypot: '' });
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Une erreur est survenue. Veuillez réessayer.');
    }
  };

  const isValid = form.name.length >= 2 && form.email && form.project_type && form.budget && form.message.length >= 10;

  const inputBase =
    'w-full bg-[#111] border border-[#222] rounded-lg px-4 py-3 text-white placeholder-[#555] focus:outline-none focus:border-[#2A3B8F] focus:ring-1 focus:ring-[#2A3B8F] transition-colors duration-200 text-sm';

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="section-container bg-[#0A0A0A]"
    >
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <p className="text-[#2A3B8F] uppercase tracking-widest text-sm mb-4">
            Votre moment
          </p>
          <h2 className="heading-lg mb-4">Discutons</h2>
          <p className="text-[#888] text-base">
            Décrivez votre projet et nous reviendrons vers vous sous 48h.
          </p>
        </motion.div>

        {/* Success State */}
        <AnimatePresence mode="wait">
          {status === 'success' ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-16"
            >
              <CheckCircle className="mx-auto mb-6 text-[#2A3B8F]" size={48} />
              <h3 className="text-xl font-semibold text-white mb-3">
                Message envoyé
              </h3>
              <p className="text-[#888] mb-8">
                Vous recevrez une confirmation par email. Nous vous répondrons sous 48 heures.
              </p>
              <button
                data-testid="contact-send-another"
                onClick={() => setStatus('idle')}
                className="text-[#2A3B8F] hover:text-[#4A5BAF] text-sm font-medium transition-colors"
              >
                Envoyer un autre message
              </button>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              onSubmit={handleSubmit}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="space-y-5"
            >
              {/* Honeypot — hidden */}
              <input
                type="text"
                name="honeypot"
                value={form.honeypot}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
              />

              {/* Row: Name + Email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-[#888] text-xs uppercase tracking-wider mb-2">
                    Nom *
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-name"
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Votre nom"
                    required
                    minLength={2}
                    maxLength={100}
                    className={inputBase}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-[#888] text-xs uppercase tracking-wider mb-2">
                    Email *
                  </label>
                  <input
                    id="contact-email"
                    data-testid="contact-email"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="votre@email.com"
                    required
                    className={inputBase}
                  />
                </div>
              </div>

              {/* Row: Project Type + Budget */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-project" className="block text-[#888] text-xs uppercase tracking-wider mb-2">
                    Type de projet *
                  </label>
                  <select
                    id="contact-project"
                    data-testid="contact-project-type"
                    name="project_type"
                    value={form.project_type}
                    onChange={handleChange}
                    required
                    className={`${inputBase} appearance-none cursor-pointer`}
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23555' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 12px center',
                    }}
                  >
                    <option value="" disabled>Sélectionnez</option>
                    {PROJECT_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-budget" className="block text-[#888] text-xs uppercase tracking-wider mb-2">
                    Budget *
                  </label>
                  <select
                    id="contact-budget"
                    data-testid="contact-budget"
                    name="budget"
                    value={form.budget}
                    onChange={handleChange}
                    required
                    className={`${inputBase} appearance-none cursor-pointer`}
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23555' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 12px center',
                    }}
                  >
                    <option value="" disabled>Sélectionnez</option>
                    {BUDGETS.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="contact-message" className="block text-[#888] text-xs uppercase tracking-wider mb-2">
                  Message *
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Décrivez votre projet, vos objectifs, et le contexte..."
                  required
                  minLength={10}
                  maxLength={5000}
                  rows={5}
                  className={`${inputBase} resize-none`}
                />
              </div>

              {/* Error Message */}
              <AnimatePresence>
                {status === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center gap-2 text-red-400 text-sm bg-red-400/10 border border-red-400/20 rounded-lg px-4 py-3"
                    data-testid="contact-error"
                  >
                    <AlertCircle size={16} />
                    <span>{errorMsg}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Submit */}
              <motion.button
                type="submit"
                data-testid="contact-submit"
                disabled={!isValid || status === 'sending'}
                className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-[#2A3B8F] text-white font-medium tracking-wide rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                whileHover={isValid && status !== 'sending' ? { backgroundColor: '#3A4BA0', scale: 1.01 } : {}}
                whileTap={isValid && status !== 'sending' ? { scale: 0.99 } : {}}
              >
                {status === 'sending' ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span>Envoi en cours...</span>
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    <span>Envoyer le message</span>
                  </>
                )}
              </motion.button>

              <p className="text-center text-[#444] text-xs mt-2">
                Nous répondons sous 48 heures aux opportunités alignées.
              </p>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default ContactSection;
